import { Component, DestroyRef, DOCUMENT, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { OcpTableData } from 'shared';
import { cssLengthToPx } from 'utilities';

import { getResolvedColumnWidth, TableColumnConfig } from '../table-header.interface';

/** Draggable/keyboard-operable handle rendered on the trailing edge of a resizable column's `<th>`. */
@Component({
  selector: 'ocp-table-column-resize-handle',
  imports: [TranslatePipe],
  template: `
    @if (column().resizable) {
      <div
        class="table-column-resize-handle"
        [class.table-column-resize-handle--active]="tableData.resizingField() === column().field"
        role="separator"
        aria-orientation="vertical"
        tabindex="0"
        [attr.aria-label]="
          column().label
            ? (column().label! | translate) + ' column resize handle'
            : 'Column resize handle'
        "
        [attr.aria-valuenow]="getColumnWidthPx()"
        [attr.aria-valuemin]="getMinWidthPx()"
        (mousedown)="onResizeStart($event)"
        (touchstart)="onResizeStart($event)"
        (keydown)="onResizeKeydown($event)"
      ></div>
    }
  `,
  styleUrl: './table-column-resize-handle.component.scss',
})
export class TableColumnResizeHandleComponent {
  protected readonly tableData: OcpTableData<unknown> = inject(OcpTableData);
  private readonly document = inject(DOCUMENT);

  readonly column = input.required<TableColumnConfig>();

  private static readonly KEYBOARD_RESIZE_STEP_PX = 8;

  // Set while a drag is in progress so it can be torn down if the component is destroyed mid-drag.
  private activeDragCleanup: (() => void) | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.activeDragCleanup?.());
  }

  protected getColumnWidthPx(): number {
    return cssLengthToPx(
      getResolvedColumnWidth(
        this.column(),
        this.tableData.columnSizing(),
        this.tableData.columnWidthPercentages()
      )
    );
  }

  protected getMinWidthPx(): number {
    return cssLengthToPx(this.column().minWidth);
  }

  protected onResizeStart(event: MouseEvent | TouchEvent): void {
    const col = this.column();
    if (!col.resizable || !col.field) return;
    const thElement = (event.currentTarget as HTMLElement).closest('th');
    if (!thElement) return;

    event.preventDefault();

    const field = col.field;
    const startX = this.getClientX(event);
    const startWidth = thElement.getBoundingClientRect().width;
    const minWidthPx = this.getMinWidthPx();

    this.freezeAllColumnWidths(thElement);
    this.tableData.startColumnResize(field);
    const previousBodyCursor = this.document.body.style.cursor;
    this.document.body.style.cursor = 'col-resize';

    const onMove = (moveEvent: MouseEvent | TouchEvent): void => {
      const currentX = this.getClientX(moveEvent);
      const newWidth = Math.max(minWidthPx, startWidth + (currentX - startX));
      this.tableData.setColumnWidth(field, newWidth);
    };

    const onEnd = (): void => {
      this.document.removeEventListener('mousemove', onMove);
      this.document.removeEventListener('mouseup', onEnd);
      this.document.removeEventListener('touchmove', onMove);
      this.document.removeEventListener('touchend', onEnd);
      this.document.removeEventListener('touchcancel', onEnd);
      this.document.body.style.cursor = previousBodyCursor;
      this.tableData.endColumnResize();
      this.activeDragCleanup = null;
    };
    this.activeDragCleanup = onEnd;

    this.document.addEventListener('mousemove', onMove);
    this.document.addEventListener('mouseup', onEnd);
    this.document.addEventListener('touchmove', onMove, { passive: false });
    this.document.addEventListener('touchend', onEnd);
    this.document.addEventListener('touchcancel', onEnd);
  }

  protected onResizeKeydown(event: KeyboardEvent): void {
    const col = this.column();
    if (!col.resizable || !col.field) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const field = col.field;
    const minWidthPx = this.getMinWidthPx();
    const currentWidth = this.getColumnWidthPx();
    const step =
      event.key === 'ArrowRight'
        ? TableColumnResizeHandleComponent.KEYBOARD_RESIZE_STEP_PX
        : -TableColumnResizeHandleComponent.KEYBOARD_RESIZE_STEP_PX;

    const thElement = (event.currentTarget as HTMLElement).closest('th');
    if (thElement) this.freezeAllColumnWidths(thElement);

    this.tableData.setColumnWidth(field, Math.max(minWidthPx, currentWidth + step));
  }

  /** On the first-ever resize, freezes every sibling column's current rendered width to px so the table stops reflowing proportionally on viewport changes. */
  private freezeAllColumnWidths(thElement: HTMLElement): void {
    const rowElement = thElement.closest('tr');
    if (!rowElement) return;

    const sizing = this.tableData.columnSizing();
    const cells = rowElement.querySelectorAll<HTMLElement>('th[data-field]');

    cells.forEach(cell => {
      const field = cell.dataset['field'];
      if (!field || sizing[field] !== undefined) return;
      this.tableData.freezeColumnWidth(field, cell.getBoundingClientRect().width);
    });
  }

  private getClientX(event: MouseEvent | TouchEvent): number {
    if ('touches' in event) {
      return event.touches[0]?.clientX ?? event.changedTouches[0]?.clientX ?? 0;
    }
    return event.clientX;
  }
}
